import { type Client, type Event, EventListener, type HttpClient } from "silver";
import { type MyonLanguageLoader } from "Lang";
import { type StorageKeyObserver } from "shared/StorageKeyObserver";
import { Signal } from "shared/Signal";
import type { Dialog } from "shared/Dialog";

export class AuthTimer {

	public readonly onBeforeLogout = EventListener.create<Event>("beforeLogout");

	private dialogIsVisible: boolean = false;
	private isLoggingOut: boolean = false;
	private logoutTimer: number = -1;
	private lastUserIsHereSignal: Signal<boolean> = new Signal();
	private readonly dialogCheckInterval: number = 5000;

	constructor(
		private readonly logoutUri: string,
		private readonly keepaliveUri: string,
		private readonly keepaliveIntervalMilliseconds: number,
		private readonly timeoutAfterIdleMilliseconds: number,
		private readonly timeToRespondMilliseconds: number,

		private pingWithCredentials: boolean,
		private httpClient: HttpClient,
		private client: Client,
		private dialog: Dialog,
		private userActivityObserver: StorageKeyObserver<number>,
		private dialogIsOpenObserver: StorageKeyObserver<boolean>,
		private lang: MyonLanguageLoader
	) { }

	public listen(): void {
		setInterval(() => { this.keepalive().catch(console.error); }, this.keepaliveIntervalMilliseconds);
		setInterval(() => { this.updateTimeoutDialog().catch(console.error); }, this.dialogCheckInterval);
		this.userActivityObserver.onChange(() => this.queueRunChecks());
		this.dialogIsOpenObserver.onChange(() => this.updateTimeoutDialog().catch(console.error));
		this.queueRunChecks();
	}

	public async keepalive(): Promise<void> {
		await this.httpClient.get(this.keepaliveUri, {
			credentials: this.pingWithCredentials ? "include" : "omit"
		});
	}

	private queueRunChecks(): void {
		clearTimeout(this.logoutTimer);

		const shouldLogoutIn = this.millisecondsUntilLogout();
		if (shouldLogoutIn <= 0) {
			return this.logout();
		}
		this.logoutTimer = setTimeout(() => this.logout(), shouldLogoutIn);
	}

	private millisecondsSinceLastActive(): number {
		const date = this.userActivityObserver.value;
		if (!date) {
			throw new Error("No date available");
		}
		return Date.now() - date;
	}

	public millisecondsUntilPrompt(): number {
		return this.timeoutAfterIdleMilliseconds - this.timeToRespondMilliseconds - this.millisecondsSinceLastActive();
	}

	public millisecondsUntilLogout(): number {
		return this.timeoutAfterIdleMilliseconds - this.millisecondsSinceLastActive();
	}

	private async updateTimeoutDialog(): Promise<void> {
		const shouldBeVisible = this.millisecondsUntilPrompt() <= 0;
		if (this.dialogIsVisible === shouldBeVisible) {
			return;
		}
		if (!shouldBeVisible) {
			this.dialogIsVisible = false;
			this.dialogIsOpenObserver.value = false;
			this.queueRunChecks();
			return this.lastUserIsHereSignal.send(true);
		}
		this.dialogIsVisible = true;
		this.dialogIsOpenObserver.value = true;
		// locking this prevents the user activity from counting (in this window only) while the dialog is up
		this.userActivityObserver.locked = true;
		const lang = await this.lang.load("Misc");
		this.lastUserIsHereSignal = new Signal();
		const userIsHere = await this.dialog.confirm(
			lang.PROMPT_PROD_TITLE(),
			lang.PROMPT_PROD_MESSAGE(),
			lang.ACTION_YES(),
			lang.ACTION_NO(),
			{
				dismissSignal: this.lastUserIsHereSignal
			}
		);
		if (!userIsHere) {
			return this.logout();
		}
		this.dialogIsVisible = false;
		this.userActivityObserver.locked = false;
		this.userActivityObserver.value = Date.now();
		this.dialogIsOpenObserver.value = false;
	}

	public logout(): void {
		if (this.isLoggingOut) {
			return;
		}
		this.onBeforeLogout.trigger({ });
		this.isLoggingOut = true;
		this.client.open(this.logoutUri);
	}
}
